import React, { useEffect, useRef } from 'react';
import { marked } from 'marked';
import config from '../config';
import { useDispatch, useSelector } from 'react-redux';
import { INSERT_INTO_TABLE } from '../slices/thunks';
import { store } from '..';

const MarkdownRenderer = ({ markdown, bot_id }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const BotDetails = useSelector((state) => state.BotDetails.Bot_Details);

  // Optional marked config (keep as you prefer)
  // marked.setOptions({ breaks: true });

  const wrapTablesWithActions = (html) => {
    return html.replace(
      /<table[\s\S]*?<\/table>/g,
      (match) => `
        <div class="markdown-table-wrapper" style="position:relative;margin-top:40px;margin-bottom:20px;">
          <div class="table-scroll-container" style="
            overflow-x:auto;
            border:1px solid #d1d5db;
            border-radius:8px;
            background:#fff;
            box-shadow:0 1px 3px rgba(0,0,0,0.1);
          ">
            ${match}
          </div>
          <button type="button" class="export-btn" title="${config.tool_tip.export_table_csv}" style="
            position:absolute;
            top:-36px;
            right:43px;
            border:1px solid #d1d5db;
            border-radius:6px;
            padding:6px 10px;
            cursor:pointer;
            transition:all 0.2s;
          ">
            <i class="ri-download-2-line"></i>
          </button>
          <button type="button" class="insert-btn" title="${config.tool_tip.insert_table_csv}" style="
            position:absolute;
            top:-36px;
            right:14px;
            border:1px solid #d1d5db;
            border-radius:6px;
            padding:6px 10px;
            cursor:pointer;
            transition:all 0.2s;
          ">
            <i class="ri-login-box-line"></i>
          </button>
        </div>`
    );
  };

  const createMarkup = (markdownText) => {
    let finalHTML = '';
    let inThink = false;

    const parts = (markdownText || '').split(/(<think>|<\/think>)/);

    for (let part of parts) {
      if (part === '<think>') {
        inThink = true;
        finalHTML += `
          <div style="margin-top:12px;margin-bottom:4px;font-size:0.9em;color:#888;">
            🧠 <strong>Bot is thinking...</strong>
          </div>`;
        continue;
      } else if (part === '</think>') {
        inThink = false;
        continue;
      }

      let rendered = marked.parse(part);
      rendered = wrapTablesWithActions(rendered);

      finalHTML += inThink
        ? `<div style="color:gray;font-style:italic;">${rendered}</div>`
        : rendered;
    }

    return { __html: finalHTML };
  };

  function tableToJSON(table) {
    if (!table) return [];
    const rows = Array.from(table.querySelectorAll('tr'));
    if (!rows.length) return [];

    const firstCells = Array.from(rows[0].querySelectorAll('th, td'));
    let headers = firstCells.map((th) => (th.textContent || '').trim());
    let dataRows = rows.slice(1);
    const hasHeaders = rows[0].querySelector('th') !== null;

    if (!hasHeaders) {
      headers = headers.map((_, i) => `col_${i + 1}`);
      dataRows = rows; // include first row as data too
    }

    return dataRows.map((row) => {
      const cells = Array.from(row.querySelectorAll('td, th')).map(
        (td) => (td.textContent || '').trim()
      );
      const obj = {};
      headers.forEach((header, i) => (obj[header || `col_${i + 1}`] = cells[i] ?? ''));
      return obj;
    });
  }

  // Build CSV with UTF-8 BOM so Chinese characters display correctly in Excel
  function buildCSVFromTable(table) {
    const lines = [];
    const rows = table.querySelectorAll('tr');

    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('th, td')).map((cell) => {
        // Use textContent to preserve Unicode (e.g., 中文) without layout artifacts
        const raw = (cell.textContent || '').replace(/\r?\n/g, '\n'); // keep linebreaks
        // Escape double quotes and wrap in quotes
        const safe = `"${raw.replace(/"/g, '""')}"`;
        return safe;
      });
      lines.push(cells.join(','));
    });

    // Prepend BOM for Excel + Chinese characters
    const BOM = '\uFEFF';
    return BOM + lines.join('\n');
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // ✅ Prevent duplicate binding (dev hot-reload or accidental re-mounts)
    if (el.dataset.markdownBound === '1') return;
    el.dataset.markdownBound = '1';

    // Add CSS for table styling
    const styleId = 'markdown-table-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .markdown-table-wrapper table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          background: #fff;
          margin: 0;
        }
        
        .markdown-table-wrapper th {
          background: #f9fafb;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e5e7eb;
          color: #374151;
          white-space: nowrap;
        }
        
        .markdown-table-wrapper td {
          padding: 12px 16px;
          border-bottom: 1px solid #e5e7eb;
          color: #1f2937;
        }
        
        .markdown-table-wrapper tr:last-child td {
          border-bottom: none;
        }
        
        .markdown-table-wrapper tr:hover {
          background: #f9fafb;
        }
        
        .markdown-table-wrapper .export-btn:hover,
        .markdown-table-wrapper .insert-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }
        
        .markdown-table-wrapper .export-btn:disabled,
        .markdown-table-wrapper .insert-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .markdown-table-wrapper .export-btn.loading i,
        .markdown-table-wrapper .insert-btn.loading i {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .table-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        
        .table-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .table-scroll-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .table-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `;
      document.head.appendChild(style);
    }

    const handleClick = (e) => {
      // ---------- EXPORT CSV ----------
      const exportBtn = e.target.closest('.export-btn');
      if (exportBtn && el.contains(exportBtn)) {
        e.preventDefault();
        e.stopPropagation();

        // ✅ prevent re-entry (double-clicks)
        if (exportBtn.classList.contains('loading')) return;
        exportBtn.classList.add('loading');
        exportBtn.setAttribute('disabled', 'true');

        try {
          const wrapper = exportBtn.closest('.markdown-table-wrapper');
          const table = wrapper?.querySelector('table');
          if (!table) return;

          const csvText = buildCSVFromTable(table);
          const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          // Optional: smarter name by index or caption
          a.download = `table_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } finally {
          exportBtn.classList.remove('loading');
          exportBtn.removeAttribute('disabled');
        }
        return;
      }

      // ---------- INSERT ACTION ----------
      const insertBtn = e.target.closest('.insert-btn');
      if (insertBtn && el.contains(insertBtn)) {
        e.preventDefault();
        e.stopPropagation();

        // prevent double-click while loading
        if (insertBtn.classList.contains('loading')) return;

        const wrapper = insertBtn.closest('.markdown-table-wrapper');
        const table = wrapper?.querySelector('table');
        if (!table) return;

        const data = tableToJSON(table);

        // Toggle loader ON
        insertBtn.classList.add('loading');
        insertBtn.setAttribute('disabled', 'true');
        const originalTitle = insertBtn.getAttribute('title') || '';
        insertBtn.setAttribute('data-original-title', originalTitle);
        insertBtn.setAttribute('title', 'Inserting...');

        const payload = {
          bot_id: store.getState().BotDetails.Bot_Details?.Bot_ID ?? bot_id,
          insertion_data: JSON.stringify(data),
          query_insertion: true,
          streaming: true,
        };

        Promise.resolve(dispatch(INSERT_INTO_TABLE(payload)))
          .catch(() => {
            // optional: toast/snackbar
          })
          .finally(() => {
            insertBtn.classList.remove('loading');
            insertBtn.removeAttribute('disabled');
            const restoreTitle = insertBtn.getAttribute('data-original-title') || '';
            insertBtn.setAttribute('title', restoreTitle);
            insertBtn.removeAttribute('data-original-title');
          });

        return;
      }
    };

    el.addEventListener('click', handleClick);
    return () => {
      el.removeEventListener('click', handleClick);
      delete el.dataset.markdownBound;
    };
  }, [dispatch, bot_id]);

  return (
    <div
      ref={containerRef}
      className="markdown-table"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={createMarkup(markdown)}
    />
  );
};

export default MarkdownRenderer;